<template>
    <div class="section-copy-modal" data-test="section-copy-modal">
        <p class="scm-hint">
            Pick a form on this site, then the section to bring here. It is added at the end of this
            form; nothing is saved until you click Save.
        </p>

        <div v-if="listLoading" class="scm-state" data-test="section-copy-list-loading">
            Loading this site's forms&hellip;
        </div>
        <div v-else-if="listError" class="scm-state scm-error" data-test="section-copy-list-error">
            <span>{{ listError }}</span>
            <button type="button" class="btn btn-sm btn-link" data-test="section-copy-retry" @click="loadForms">Retry</button>
        </div>
        <div v-else-if="!forms.length" class="scm-state" data-test="section-copy-empty">
            No other forms on this site.
        </div>

        <template v-else>
            <label class="scm-label" for="section-copy-form-select">Form</label>
            <select
                id="section-copy-form-select"
                class="form-control scm-select"
                data-test="section-copy-form-select"
                v-model="selectedFormId"
                @change="loadForm"
            >
                <option value="" disabled>-- Choose a form --</option>
                <option v-for="form in forms" :key="form.FormIDForWeb" :value="form.FormIDForWeb">{{ form.Name || form.FormIDForWeb }}</option>
            </select>

            <div v-if="formLoading" class="scm-state" data-test="section-copy-form-loading">
                Loading the form&hellip;
            </div>
            <div v-else-if="formError" class="scm-state scm-error" data-test="section-copy-form-error">
                <span>{{ formError }}</span>
                <button type="button" class="btn btn-sm btn-link" data-test="section-copy-form-retry" @click="loadForm">Retry</button>
            </div>
            <div v-else-if="sourceConfig && !sections.length" class="scm-state" data-test="section-copy-no-sections">
                This form has no sections to copy.
            </div>

            <div v-else-if="sections.length" class="scm-sections" role="radiogroup" aria-label="Section to copy">
                <div
                    v-for="section in sections"
                    :key="section.id"
                    class="scm-section"
                    :class="{ 'is-selected': section.id === selectedSectionId }"
                    role="radio"
                    tabindex="0"
                    :aria-checked="section.id === selectedSectionId ? 'true' : 'false'"
                    data-test="section-copy-section"
                    :data-section-id="section.id"
                    @click="selectSection(section.id)"
                    @keydown.enter.prevent="selectSection(section.id)"
                    @keydown.space.prevent="selectSection(section.id)"
                >
                    <div class="scm-section-head">
                        <input
                            type="radio"
                            name="section-copy-section"
                            tabindex="-1"
                            :value="section.id"
                            :checked="section.id === selectedSectionId"
                            data-test="section-copy-section-radio"
                            @click.stop="selectSection(section.id)"
                        >
                        <span class="scm-section-title" data-test="section-copy-section-headline">{{ section.title }}</span>
                        <span class="scm-section-meta">{{ sectionTypeName(section.type) }} &middot; {{ fieldCountText(section.fieldCount) }}</span>
                    </div>

                    <div v-for="(group, gi) in section.groups" :key="section.id + '-g-' + gi" class="scm-group">
                        <div v-if="group.tabName !== null" class="scm-tab" data-test="section-copy-tab">{{ group.tabName }}</div>
                        <div v-for="field in group.fields" :key="field.id" class="scm-field" data-test="section-copy-field">
                            <span class="scm-field-label">{{ field.label }}</span>
                            <span class="scm-field-type">{{ field.typeName }}</span>
                        </div>
                        <div v-if="!group.fields.length" class="scm-field scm-muted">No fields</div>
                    </div>
                    <div v-if="!section.groups.length" class="scm-field scm-muted">No fields</div>
                </div>
            </div>
        </template>

        <div class="scm-actions">
            <button type="button" class="btn btn-sm btn-secondary" data-test="section-copy-cancel" @click="cancel">Cancel</button>
            <button
                type="button"
                class="btn btn-sm btn-primary ml-2"
                data-test="section-copy-submit"
                :disabled="!canCopy"
                @click="copy"
            >Copy section</button>
        </div>
    </div>
</template>

<script>
/**
 * "Copy a section from another form" — the picker GlobalModal shows for AddSectionControl.
 *
 * Lists this site's forms (minus the one being edited), loads the picked form, previews every
 * section (headline, type, each field's label + control type; tab sections grouped by tab) and
 * emits saveAndClose { sourceConfig, sectionId }. AddSectionControl relays that to
 * form-builder-event-handler.js (sectionCopyFromForm), which runs graftSection on the builder's
 * formData. Nothing is stored here or there: the office still has to click Save, so EditForm /
 * CreateForm's save gates (name, OD imaging, OD unique mapping, ...) run on the result.
 *
 * Both fetches pass `silent: true` (store/forms.js): the global alert/setLoading would raise
 * EditForm's "Saving form" overlay over this modal.
 */
import { CONTROLS } from "../../../configs/controls";
import { sanitizeFormConfig } from "../../../libraries/sanitize-config";
import { describeSections } from "../../../libraries/section-copy-describe";

const SECTION_TYPE_NAMES = {
    normal: 'Section',
    toggleable: 'Toggleable section',
    tab: 'Tabbed section',
};

/** forms/getForms resolves { forms, categories } (or a bare array); undefined on a failed request. */
function formsFrom(result) {
    if (Array.isArray(result)) return result;
    if (result && Array.isArray(result.forms)) return result.forms;
    if (result && result.data && Array.isArray(result.data.forms)) return result.data.forms;
    return null;
}

export default {
    name: "SectionCopyModal",
    props: {
        // injected by AddSectionControl through GlobalModal: { siteID, excludeFormId }
        dataPackage: { type: Object, default: () => ({}) },
        // the builder's form (the copy target), passed by GlobalModal
        formData: { type: Object, default: () => ({}) },
        permissions: { type: Object, default: () => ({}) }
    },
    data: () => ({
        forms: [],
        listLoading: false,
        listError: '',
        selectedFormId: '',
        formLoading: false,
        formError: '',
        sourceConfig: null,
        selectedSectionId: '',
        // drops a stale getForm answer when the office switches forms mid-load
        formRequestSeq: 0,
    }),
    computed: {
        copySiteID() {
            return (this.dataPackage && this.dataPackage.siteID) || ''
        },
        copyExcludeFormId() {
            return (this.dataPackage && this.dataPackage.excludeFormId) || ''
        },
        sections() {
            if (!this.sourceConfig) return []
            return describeSections(this.sourceConfig, type => (CONTROLS[type] && CONTROLS[type].name) || type)
        },
        canCopy() {
            return !this.formLoading
                && !!this.sourceConfig
                && !!this.selectedSectionId
                && this.sections.some(s => s.id === this.selectedSectionId)
        }
    },
    created() {
        this.loadForms()
    },
    methods: {
        async loadForms() {
            this.listLoading = true
            this.listError = ''
            this.forms = []
            try {
                if (!this.copySiteID) {
                    throw new Error('no site')
                }
                const result = await this.$store.dispatch('forms/getForms', {
                    siteID: this.copySiteID,
                    includeConfigData: false,
                    silent: true,
                })
                const list = formsFrom(result)
                if (!list) {
                    throw new Error('no forms list')
                }
                const exclude = this.copyExcludeFormId
                this.forms = list
                    .filter(f => f && f.FormIDForWeb && f.FormIDForWeb !== exclude)
                    .slice()
                    .sort((a, b) => String(a.Name || '').localeCompare(String(b.Name || ''), undefined, { sensitivity: 'base' }))
            } catch (err) {
                this.listError = "Couldn't load this site's forms."
            } finally {
                this.listLoading = false
            }
        },

        async loadForm() {
            const formIDForWeb = this.selectedFormId
            const seq = ++this.formRequestSeq
            this.sourceConfig = null
            this.selectedSectionId = ''
            this.formError = ''
            if (!formIDForWeb) return
            this.formLoading = true
            try {
                const res = await this.$store.dispatch('forms/getForm', { formIDForWeb, silent: true })
                if (seq !== this.formRequestSeq) return
                const raw = res && res.data && res.data.config && res.data.config.ConfigData
                if (!raw) {
                    throw new Error('no ConfigData')
                }
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
                const { sanitized } = sanitizeFormConfig(parsed, { controlDefinitions: CONTROLS })
                if (!sanitized || typeof sanitized !== 'object') {
                    throw new Error('unreadable ConfigData')
                }
                this.sourceConfig = sanitized
            } catch (err) {
                if (seq !== this.formRequestSeq) return
                this.formError = "Couldn't load that form."
            } finally {
                if (seq === this.formRequestSeq) this.formLoading = false
            }
        },

        selectSection(sectionId) {
            this.selectedSectionId = sectionId
        },

        sectionTypeName(type) {
            return SECTION_TYPE_NAMES[type] || 'Section'
        },

        fieldCountText(n) {
            return `${n} ${n === 1 ? 'field' : 'fields'}`
        },

        copy() {
            if (!this.canCopy) return
            this.$emit('saveAndClose', { sourceConfig: this.sourceConfig, sectionId: this.selectedSectionId })
        },

        cancel() {
            this.$emit('close', false)
        }
    }
}
</script>

<style lang="scss" scoped>
.section-copy-modal {
    padding: 4px 2px;
}
.scm-hint {
    font-size: 14px;
    color: #374151;
    margin-bottom: 12px;
}
.scm-state {
    color: #6b7280;
    font-style: italic;
    padding: 12px 4px;
}
.scm-error {
    color: #b91c1c;
    font-style: normal;
}
.scm-label {
    display: block;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
}
.scm-select {
    max-width: 100%;
    margin-bottom: 12px;
}
/* only the list scrolls, so Cancel / Copy section stay in view */
.scm-sections {
    margin-top: 4px;
    max-height: 55vh;
    overflow-y: auto;
    padding: 2px;
}
.scm-section {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    margin-bottom: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.12s, box-shadow 0.12s;

    &:hover {
        border-color: #c4b5fd;
    }
    &:focus {
        outline: none;
        border-color: #8b5cf6;
        box-shadow: 0 0 0 2px #ede9fe;
    }
    &.is-selected {
        border-color: #8b5cf6;
        box-shadow: 0 0 0 2px #ede9fe;
    }
}
.scm-section-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    background: #f8fafc;
    padding: 8px 12px;
    border-bottom: 1px solid #e5e7eb;
}
.scm-section-title {
    font-weight: 700;
    color: #1f2937;
    overflow-wrap: anywhere;
}
.scm-section-meta {
    margin-left: auto;
    font-size: 12px;
    color: #6b7280;
}
.scm-group {
    padding: 4px 12px 6px;
}
.scm-tab {
    font-size: 12px;
    font-weight: 700;
    color: #6d28d9;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    padding: 6px 0 2px;
}
.scm-field {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    font-size: 13px;
    color: #4b5563;
    border-bottom: 1px dashed #f1f5f9;

    &:last-child {
        border-bottom: none;
    }
}
.scm-field-label {
    overflow-wrap: anywhere;
}
.scm-field-type {
    flex-shrink: 0;
    color: #9ca3af;
    font-size: 12px;
}
.scm-muted {
    font-style: italic;
    color: #9ca3af;
}
.scm-actions {
    margin-top: 10px;
    text-align: right;
}
</style>
